// Original source is https://github.com/TobiasNickel/tXml/tree/master
// Slightly modified

/*
 * MIT License
 *
 * Copyright (c) 2017 Evan Wallace
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

export type TNode = {
  tagName: string;
  attributes: Record<string, string>;
  children: (TNode | string)[];
};

export type TParseOptions = {
  noChildNodes?: string[];
};

// Literal characters, for indexOf/comparison against string positions.
const Lit = {
  OpenBracket: "<",
  CloseBracket: ">",
} as const;

// Character codes, for charCodeAt comparison.
// Kept separate from Lit: a single enum mixing strings and numbers typed
// indexOf(Char.CloseBracket) as a union and invited passing the wrong one.
const Code = {
  OpenBracket: "<".charCodeAt(0),
  CloseBracket: ">".charCodeAt(0),
  Minus: "-".charCodeAt(0),
  Slash: "/".charCodeAt(0),
  Exclamation: "!".charCodeAt(0),
  SingleQuote: "'".charCodeAt(0),
  DoubleQuote: '"'.charCodeAt(0),
  OpenSquareBracket: "[".charCodeAt(0),
  CloseSquareBracket: "]".charCodeAt(0),
} as const;

/**
 * parseXML / html into a DOM Object. with no validation and some failur tolerance
 * @param {string} string your XML to parse
 * @param {TParseOptions} [options]  all other options:
 * @return {(TNode | string)[]}
 */
export function parse(string: string, options?: TParseOptions): (TNode | string)[] {
  options = options || {};

  let pos = 0;

  /**
   * parsing a list of entries
   */
  function parseChildren(tagName: string): (TNode | string)[] {
    const children: (TNode | string)[] = [];
    while (string[pos]) {
      if (string.charCodeAt(pos) === Code.OpenBracket) {
        if (string.charCodeAt(pos + 1) === Code.Slash) {
          const closeStart = pos + 2;
          pos = string.indexOf(Lit.CloseBracket, pos);
          if (pos === -1) {
            // Unterminated close tag: salvage what we have.
            pos = string.length;
            return children;
          }

          const closeTag = string.substring(closeStart, pos);
          if (closeTag.indexOf(tagName) === -1) {
            // Failure-tolerant by design: GDPR exports contain hand-rolled
            // HTML with mismatched tags. Warn and unwind to the parent,
            // which will re-read this close tag.
            const lines = string.substring(0, pos).split("\n");
            console.warn(
              `Unexpected close tag </${closeTag}>, expected </${tagName}> ` +
                `at line ${lines.length}, column ${lines[lines.length - 1].length + 1}`,
            );
            pos = closeStart - 2;
            return children;
          }

          pos += 1;
          return children;
        } else if (string.charCodeAt(pos + 1) === Code.Exclamation) {
          if (string.charCodeAt(pos + 2) === Code.Minus) {
            // comment support
            while (
              pos !== -1 &&
              !(
                string.charCodeAt(pos) === Code.CloseBracket &&
                string.charCodeAt(pos - 1) === Code.Minus &&
                string.charCodeAt(pos - 2) === Code.Minus
              )
            ) {
              pos = string.indexOf(Lit.CloseBracket, pos + 1);
            }
            if (pos === -1) {
              pos = string.length;
            }
          } else if (
            string.charCodeAt(pos + 2) === Code.OpenSquareBracket &&
            string.charCodeAt(pos + 8) === Code.OpenSquareBracket &&
            // substring's 2nd arg is an END index, not a length: the old
            // substring(pos + 3, 5) returned "" for any pos > 2, so CDATA
            // was never detected.
            string.slice(pos + 3, pos + 8).toLowerCase() === "cdata"
          ) {
            const cdataEnd = string.indexOf("]]>", pos);
            if (cdataEnd === -1) {
              children.push(string.slice(pos + 9));
              pos = string.length;
            } else {
              children.push(string.slice(pos + 9, cdataEnd));
              pos = cdataEnd + 3;
            }
          } else {
            // doctype and similar
            let encapsuled = false;
            while ((string.charCodeAt(pos) !== Code.CloseBracket || encapsuled === true) && string[pos]) {
              if (string.charCodeAt(pos) === Code.OpenSquareBracket) {
                encapsuled = true;
              } else if (encapsuled === true && string.charCodeAt(pos) === Code.CloseSquareBracket) {
                encapsuled = false;
              }
              pos++;
            }
            pos++;
          }
        } else {
          const node = parseNode();
          children.push(node);
        }
      } else {
        const text = parseText();
        if (text.trim().length > 0) children.push(text);
      }
    }
    return children;
  }

  /**
   *    returns the text outside of texts until the first '<'
   */
  function parseText() {
    const start = pos;
    pos = string.indexOf(Lit.OpenBracket, pos) - 1;
    if (pos === -2) pos = string.length;
    return string.slice(start, pos + 1);
  }

  /**
   *    returns text until the first nonAlphabetic letter
   */
  const nameSpacer = "\r\n\t>/= ";

  function parseName() {
    const start = pos;
    while (nameSpacer.indexOf(string[pos]) === -1 && string[pos]) {
      pos++;
    }
    return string.slice(start, pos);
  }
  /**
   * is parsing a node, including tagName, Attributes and its children,
   * to parse children it uses the parseChildren again, that makes the parsing recursive
   */
  const noChildNodes = options.noChildNodes || ["img", "br", "input", "meta", "link", "hr"];

  function parseNode(): TNode {
    pos++;
    const tagName = parseName();
    const attributes: Record<string, string | null> = {};
    let children: (TNode | string)[] = [];

    while (string.charCodeAt(pos) !== Code.CloseBracket && string[pos]) {
      const c = string.charCodeAt(pos);
      // A-Za-z
      if ((c > 64 && c < 91) || (c > 96 && c < 123)) {
        const name = parseName();
        let code = string.charCodeAt(pos);
        while (
          code &&
          code !== Code.SingleQuote &&
          code !== Code.DoubleQuote &&
          // A-Za-z
          !((code > 64 && code < 91) || (code > 96 && code < 123)) &&
          code !== Code.CloseBracket
        ) {
          pos++;
          code = string.charCodeAt(pos);
        }
        let value: string | null;
        if (code === Code.SingleQuote || code === Code.DoubleQuote) {
          value = parseString();
          if (pos === -1) {
            return { tagName, attributes, children };
          }
        } else {
          value = null;
          pos--;
        }
        attributes[name] = value;
      }
      pos++;
    }

    if (string.charCodeAt(pos - 1) !== Code.Slash) {
      if (tagName === "script") {
        const start = pos + 1;
        pos = string.indexOf("</script>", pos);
        // Unclosed script: the old code sliced to -1 and rewound pos to 8,
        // spinning the parser forever.
        if (pos === -1) pos = string.length;
        children = [string.slice(start, pos)];
        pos += 9;
      } else if (tagName === "style") {
        const start = pos + 1;
        pos = string.indexOf("</style>", pos);
        if (pos === -1) pos = string.length;
        children = [string.slice(start, pos)];
        pos += 8;
      } else if (noChildNodes.indexOf(tagName) === -1) {
        pos++;
        children = parseChildren(tagName);
      }
    }

    return { tagName, attributes, children };
  }

  /**
   * is parsing a string, that starts with a char and with the same usually  ' or "
   */

  function parseString() {
    const startChar = string[pos];
    const startpos = pos + 1;
    pos = string.indexOf(startChar, startpos);
    return string.slice(startpos, pos);
  }

  return parseChildren("");
}

type XmlJson = number | string | { [key: string]: XmlJson | XmlJson[] };

/**
 * transform the DomObject to an object that is like the object of PHP`s simple_xmp_load_*() methods.
 * this format helps you to write that is more likely to keep your program working, even if there a small changes in the XML schema.
 * be aware, that it is not possible to reproduce the original xml from a simplified version, because the order of elements is not saved.
 * therefore your program will be more flexible and easier to read.
 *
 * @param {(string | TNode)[]} children the childrenList
 */
export function simplify(children: (string | TNode)[]) {
  const out: XmlJson = {};
  if (!children.length) {
    return "";
  }

  if (children.length === 1 && typeof children[0] === "string") {
    return children[0];
  }
  // map each object
  children.forEach((child) => {
    if (typeof child !== "object") {
      return;
    }
    if (!out[child.tagName]) out[child.tagName] = [];
    const kids = simplify(child.children);
    const item = out[child.tagName];
    if (Array.isArray(item)) {
      item.push(kids);
    }
    if (Object.keys(child.attributes).length > 0 && typeof kids !== "string") {
      kids._attributes = child.attributes;
    }
  });

  for (const i in out) {
    const item = out[i];
    if (Array.isArray(item) && item.length === 1) {
      out[i] = item[0];
    }
  }

  return out;
}
